import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Badge } from '../ui/Badge';

/**
 * FormLayoutRenderer - Renders forms based on layout configuration
 * Supports MUI-inspired sections, responsive grids, subsections, and special display modes
 */
const FormLayoutRenderer = ({ 
  layout, 
  fields, 
  renderField, 
  formData = {},
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [activeTab, setActiveTab] = useState(layout.sections?.[0]?.id);

  // Toggle section collapse
  const toggleSection = (sectionId) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionId)) {
      newCollapsed.delete(sectionId);
    } else {
      newCollapsed.add(sectionId);
    }
    setCollapsedSections(newCollapsed);
  };

  // Get field definition by name
  const getFieldDef = (fieldName) => {
    return fields.find(f => f.name === fieldName);
  };

  // Simple and reliable CSS Grid classes
  const getGridClasses = (columns, spacing = 'gap-4') => {
    // Use simple grid patterns that definitely work with Tailwind
    const gridCols = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-3', 
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      12: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' // Simplified 12-col to 3-col max
    };
    return `grid ${spacing} ${gridCols[columns] || 'grid-cols-1'}`;
  };

  // Simplified responsive span classes using standard Tailwind classes
  const getSpanClasses = (fieldLayout, totalColumns) => {
    const { span = 1, xs, sm, md, lg, xl } = fieldLayout;
    
    // For responsive breakpoints, convert to simple patterns
    if (xs || sm || md || lg || xl) {
      // Convert MUI-style responsive to simple Tailwind patterns
      const mdCols = md || sm || xs || 12;
      
      if (mdCols === 12) return 'col-span-full'; // Full width
      if (mdCols === 8) return 'col-span-full md:col-span-2'; // 2/3 width on md+
      if (mdCols === 6) return 'col-span-full md:col-span-1'; // 1/2 width on md+
      if (mdCols === 4) return 'col-span-full md:col-span-1'; // 1/3 width on md+
      
      return 'col-span-full md:col-span-1'; // Default to full on mobile, 1 col on desktop
    }
    
    // Fallback to simple span logic
    if (span >= totalColumns) return 'col-span-full';
    
    const spanClasses = {
      1: 'col-span-1',
      2: 'col-span-full md:col-span-2',
      3: 'col-span-full',
      4: 'col-span-full',
      6: 'col-span-full',
      8: 'col-span-full',
      12: 'col-span-full'
    };
    return spanClasses[span] || 'col-span-1';
  };

  // Render individual field with layout
  const renderLayoutField = (fieldLayout, sectionColumns, sectionReadonly = false) => {
    const fieldDef = getFieldDef(fieldLayout.name);
    if (!fieldDef) return null;

    // Skip rendering if field has conditional display and condition is not met
    if (fieldLayout.condition && !evaluateCondition(fieldLayout.condition, formData)) {
      return null;
    }

    const finalFieldDef = {
      ...fieldDef,
      readonly: sectionReadonly || fieldDef.readonly || fieldLayout.readonly
    };

    return (
      <div 
        key={fieldLayout.name}
        className={`${getSpanClasses(fieldLayout, sectionColumns)} ${fieldLayout.className || ''}`}
      >
        {renderField(finalFieldDef)}
      </div>
    );
  };

  // Evaluate conditional display logic
  const evaluateCondition = (condition, data) => {
    if (!condition) return true;
    
    const { field, operator, value } = condition;
    const fieldValue = data[field];
    
    switch (operator) {
      case 'equals': return fieldValue === value;
      case 'not_equals': return fieldValue !== value;
      case 'exists': return fieldValue != null && fieldValue !== '';
      case 'not_exists': return fieldValue == null || fieldValue === '';
      case 'true': return Boolean(fieldValue);
      case 'false': return !Boolean(fieldValue);
      default: return true;
    }
  };

  // Render subsection
  const renderSubsection = (subsection, sectionColumns, sectionReadonly) => {
    return (
      <div key={subsection.title} className="space-y-4">
        <h4 className={`text-sm font-semibold ${theme.text.primary} border-b ${theme.border.light} pb-2`}>
          {subsection.title}
        </h4>
        <div className={getGridClasses(sectionColumns)}>
          {subsection.fields.map(fieldLayout => 
            renderLayoutField(fieldLayout, sectionColumns, sectionReadonly)
          )}
        </div>
      </div>
    );
  };

  // Render section content
  const renderSectionContent = (section) => {
    const isCollapsed = collapsedSections.has(section.id);
    if (isCollapsed) return null;

    const sectionColumns = section.columns || 2;
    const sectionReadonly = section.readonly || false;

    return (
      <div className="space-y-6">
        {/* Direct fields */}
        {section.fields && section.fields.length > 0 && (
          <div className={getGridClasses(sectionColumns)}>
            {section.fields.map(fieldLayout => 
              renderLayoutField(fieldLayout, sectionColumns, sectionReadonly)
            )}
          </div>
        )}

        {/* Subsections */}
        {section.subsections && section.subsections.map(subsection =>
          renderSubsection(subsection, sectionColumns, sectionReadonly)
        )}
      </div>
    );
  };

  // Render special display section (like tracking info)
  const renderDisplaySection = (section) => {
    // Check if section should be displayed based on data
    const shouldDisplay = section.displayCondition ? 
      evaluateCondition(section.displayCondition, formData) : 
      true;
      
    if (!shouldDisplay) return null;

    const elevation = section.elevation || 0;
    const elevationClass = elevation > 0 ? 'shadow-md' : 'shadow-sm';

    return (
      <div key={section.id} className={`
        ${section.variant === 'tracking' ? 'bg-gray-50 dark:bg-gray-800' : theme.colors.surface}
        border ${theme.border.light} rounded-lg ${elevationClass}
        ${section.className || ''}
      `}>
        {/* Special Header for Tracking */}
        <div className="px-6 py-4">
          <div className="flex items-center gap-3 mb-4">
            {section.icon && (
              <span className="text-xl">{section.icon}</span>
            )}
            <div>
              <h3 className={`text-lg font-semibold ${
                section.variant === 'tracking' ? 'text-gray-600 dark:text-gray-300' : theme.text.primary
              }`}>
                {section.title}
              </h3>
              {section.description && (
                <p className={`text-sm ${theme.text.secondary} mt-1`}>
                  {section.description}
                </p>
              )}
            </div>
          </div>

          {/* Display Content */}
          {renderSectionContent(section)}

          {/* Footer Note */}
          {section.footerNote && (
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 italic flex items-center gap-2">
                <span>ℹ️</span>
                {section.footerNote}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render regular section (MUI Paper-inspired)
  const renderSection = (section) => {
    const isCollapsed = collapsedSections.has(section.id);
    const canCollapse = section.collapsible !== false;

    // Use special renderer for display sections
    if (section.type === 'display') {
      return renderDisplaySection(section);
    }

    const elevation = section.elevation || 1;
    const elevationClass = {
      0: '',
      1: 'shadow-sm',
      2: 'shadow-md', 
      3: 'shadow-lg'
    }[elevation] || 'shadow-sm';

    return (
      <div key={section.id} className={`
        border rounded-lg ${theme.border.light} ${theme.colors.surface} ${elevationClass}
        ${section.className || ''}
      `}>
        {/* Section Header */}
        <div 
          className={`
            px-6 py-4 border-b ${theme.border.light}
            ${canCollapse ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''}
          `}
          onClick={canCollapse ? () => toggleSection(section.id) : undefined}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {section.icon && (
                <span className="text-lg">{section.icon}</span>
              )}
              <div>
                <h3 className={`text-lg font-semibold ${
                  section.headerColor === 'primary' ? 'text-blue-600 dark:text-blue-400' : theme.text.primary
                }`}>
                  {section.title}
                </h3>
                {section.description && (
                  <p className={`text-sm ${theme.text.secondary} mt-1`}>
                    {section.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {section.readonly && (
                <Badge variant="secondary" size="sm">
                  Read Only
                </Badge>
              )}
              {canCollapse && (
                <button type="button" className={`p-1 rounded ${theme.text.secondary} hover:${theme.text.primary}`}>
                  <svg 
                    className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-0' : 'rotate-180'}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Section Content */}
        <div className="px-6 py-6">
          {renderSectionContent(section)}
        </div>
      </div>
    );
  };

  // Render tabs
  const renderTabs = () => {
    return (
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className={`border-b ${theme.border.light}`}>
          <nav className="flex space-x-8">
            {layout.sections.map(section => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveTab(section.id)}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === section.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }
                `}
              >
                {section.icon && <span className="mr-2">{section.icon}</span>}
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {layout.sections.map(section => (
            <div
              key={section.id}
              className={activeTab === section.id ? 'block' : 'hidden'}
            >
              <div className="space-y-6">
                {section.description && (
                  <p className={`text-sm ${theme.text.secondary}`}>
                    {section.description}
                  </p>
                )}
                {renderSectionContent(section)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Main render logic
  if (!layout || !layout.sections) {
    return <div className={className}>No layout configuration provided</div>;
  }

  return (
    <div className={`space-y-6 ${className}`} {...props}>
      {layout.mode === 'tabs' ? renderTabs() : (
        layout.sections.map(renderSection)
      )}
    </div>
  );
};

export default FormLayoutRenderer;
